import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Loader2, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { fmtMoney, monthLabel, useSetEnrollment, useTankRentalEstimate, useTankRentalRoster, type RosterRow } from '@/hooks/useTankRentals';

type Filter = 'on' | 'off' | 'all';

/**
 * Who is on the CO₂ tank rental program. Every account with cylinder
 * activity in the last 12 months is listed, on or off, so putting someone ON
 * is a switch on this page and not a search somewhere else. The "Off" tab is
 * the work queue: customers taking tanks who are not being billed for them.
 */
export default function RentalsRoster() {
  const { data, isLoading, error } = useTankRentalRoster();
  const estimate = useTankRentalEstimate(null);
  const setEnrollment = useSetEnrollment();
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState<Filter>('on');
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const rows = useMemo(() => {
    const all = data?.roster ?? [];
    const needle = q.trim().toLowerCase();
    return all.filter((r) => {
      if (filter === 'on' && !r.enrolled) return false;
      if (filter === 'off' && r.enrolled) return false;
      if (!needle) return true;
      return r.customer_name.toLowerCase().includes(needle) || r.qbo_customer_id.includes(needle);
    });
  }, [data, q, filter]);

  const counts = useMemo(() => {
    const all = data?.roster ?? [];
    return { on: all.filter((r) => r.enrolled).length, off: all.filter((r) => !r.enrolled).length, all: all.length };
  }, [data]);

  const toggle = async (r: RosterRow) => {
    setErr(null);
    setBusy(r.qbo_customer_id);
    try {
      await setEnrollment.mutateAsync({ qbo_customer_id: r.qbo_customer_id, enrolled: !r.enrolled });
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-xl font-bold tracking-display-tight text-brix-ink">Gas cylinder rental fees</h1>
          <p className="mt-1 text-sm text-brix-muted">Who is billed a monthly rental fee for the gas cylinders they hold: CO₂, nitrogen blends and every other gas. Switch a customer on or off here; open one to set their rates and emails.</p>
        </div>
        <Button asChild>
          <Link to="/audit/rentals/run">This month’s invoices →</Link>
        </Button>
      </div>

      <Link to="/audit/rentals/estimate" className="mt-4 block rounded-xl border border-brix-border bg-brix-surface p-4 shadow-card transition hover:border-brix-primary">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-[11px] uppercase tracking-wide text-brix-muted">Estimated monthly rental fees{estimate.data ? ` · ${monthLabel(estimate.data.period)}` : ''}</div>
            <div className="mt-1 font-display text-2xl font-bold text-brix-ink">
              {estimate.isLoading ? <span className="inline-flex items-center gap-2 text-base text-brix-muted"><Loader2 className="h-4 w-4 animate-spin" /> computing from today’s counts…</span>
                : estimate.error ? <span className="text-base text-brix-warning">couldn’t compute: {estimate.error.message}</span>
                : fmtMoney(estimate.data?.total ?? 0)}
            </div>
            {estimate.data && (
              <div className="mt-1 text-xs text-brix-muted">
                {estimate.data.billable} customers would be billed · {estimate.data.zero} at $0 · {estimate.data.held} held{estimate.data.held ? ' (inactive in QuickBooks)' : ''} · if the run went out right now
              </div>
            )}
          </div>
          <span className="inline-flex items-center gap-1 text-sm font-medium text-brix-primary">See every customer’s estimate <ChevronRight className="h-4 w-4" /></span>
        </div>
      </Link>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        {(['on', 'off', 'all'] as Filter[]).map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setFilter(f)}
            className={`rounded-full border px-3 py-1 text-xs font-medium ${filter === f ? 'border-brix-primary bg-brix-primary text-white' : 'border-brix-border bg-brix-surface text-brix-ink'}`}
          >
            {f === 'on' ? `On the program · ${counts.on}` : f === 'off' ? `Not billed · ${counts.off}` : `Everyone · ${counts.all}`}
          </button>
        ))}
        <div className="relative ml-auto min-w-[220px] flex-1 sm:flex-none">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-brix-muted" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or QuickBooks #" className="pl-9" />
        </div>
      </div>

      {filter === 'off' && (
        <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          These accounts have taken or returned cylinders in the last 12 months and are <b>not</b> on a rental. Switch one on and it is billed from next month’s run.
        </p>
      )}
      {err && <p className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{err}</p>}

      <div className="mt-4 overflow-hidden rounded-xl border border-brix-border bg-brix-surface shadow-card">
        {isLoading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-brix-muted"><Loader2 className="h-5 w-5 animate-spin" /><span className="text-sm">Loading…</span></div>
        ) : error ? (
          <div className="p-4 text-sm text-brix-warning">Couldn’t load the roster: {error.message}</div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-center text-sm text-brix-muted">Nobody matches.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-brix-surface-alt text-left text-[11px] uppercase tracking-wide text-brix-muted">
              <tr>
                <th className="px-3 py-2">Customer</th>
                <th className="px-3 py-2">On hand</th>
                <th className="hidden px-3 py-2 md:table-cell">Last rental</th>
                <th className="hidden px-3 py-2 md:table-cell">Last delivery</th>
                <th className="px-3 py-2 text-right">Program</th>
                <th className="w-8" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.qbo_customer_id} className="border-t border-brix-border">
                  <td className="px-3 py-2">
                    <Link to={`/audit/rentals/${r.qbo_customer_id}`} className="font-medium text-brix-ink hover:underline">{r.customer_name}</Link>
                    <div className="text-[11px] text-brix-muted">
                      QB #{r.qbo_customer_id}
                      {r.qbo_active === false && <span className="ml-2 rounded bg-red-100 px-1.5 py-0.5 text-red-800">inactive in QuickBooks</span>}
                      {r.portal_customer_id && <span className="ml-2 rounded bg-slate-100 px-1.5 py-0.5">portal</span>}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-xs text-brix-ink">
                    {(r.gases ?? []).length === 0 ? <span className="text-brix-muted">—</span> : (r.gases ?? []).map((g) => (
                      <div key={g.label} className={g.on_hand < 0 ? 'text-red-700' : ''}>{g.on_hand} × {g.label}</div>
                    ))}
                  </td>
                  <td className="hidden px-3 py-2 text-xs text-brix-muted md:table-cell">{r.last_rental_doc ? <>{r.last_rental_doc}<br />{r.last_rental_date}</> : 'never'}</td>
                  <td className="hidden px-3 py-2 text-xs text-brix-muted md:table-cell">{r.last_delivery ?? '—'}<div>{r.deliveries_12mo} in 12 mo</div></td>
                  <td className="px-3 py-2 text-right">
                    <button
                      type="button"
                      role="switch"
                      aria-checked={r.enrolled}
                      disabled={busy === r.qbo_customer_id}
                      onClick={() => void toggle(r)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 ${r.enrolled ? 'bg-emerald-500' : 'bg-slate-300'}`}
                    >
                      <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${r.enrolled ? 'translate-x-5' : 'translate-x-0.5'}`} />
                    </button>
                    <div className="mt-0.5 text-[10px] text-brix-muted">{r.enrolled ? 'billed monthly' : 'off'}</div>
                  </td>
                  <td className="px-2"><Link to={`/audit/rentals/${r.qbo_customer_id}`}><ChevronRight className="h-4 w-4 text-brix-muted" /></Link></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
